import { TypedDocumentNode, gql } from '@apollo/client';
import { useEffect } from 'react';
import { FormattedMessage } from 'react-intl';
import { generatePath } from 'react-router-dom';
import styled from 'styled-components';

import { Link, useTypedParams } from '@sorare/routing';

import { Button } from '@sorare/core/src/atoms/buttons/Button';
import { Horizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import { LabelM, Text16, Title4 } from '@sorare/core/src/atoms/typography';
import { Avatar } from '@sorare/core/src/components/user/Avatar';
import { MyClubLink } from '@sorare/core/src/components/user/MyClubLink';
import { UserName } from '@sorare/core/src/components/user/UserName';
import {
  FOOTBALL_PLAY,
  MLB_PLAY_PRO_FIXTURE_MY_LINEUPS_LINEUP,
} from '@sorare/core/src/constants/__generated__/routes';
import { useSeoContext } from '@sorare/core/src/contexts/seo';
import { idFromObject } from '@sorare/core/src/gql/idFromObject';
import { useQuery } from '@sorare/core/src/hooks/graphql/useQuery';
import { glossary } from '@sorare/core/src/lib/glossary';

import { LineupDate } from '@sorare/gameplay/src/components/lineup/LineupDate';
import { useFootballEvents } from '@sorare/gameplay/src/lib/events';
import getLineupDisplayName from '@sorare/gameplay/src/lib/lineup/getLineupDisplayName';

import { So5Lineup } from 'components/lineup/So5Lineup';

import {
  BaseballLineupSharingQuery,
  BaseballLineupSharingQueryVariables,
} from './__generated__/page.graphql';
import { PageParams } from './__generated__/routeParams';

const Root = styled(Vertical).attrs({ center: true })`
  height: var(--100vh);
  padding: var(--double-unit);
  background-color: var(--c-neutral-200);
`;
const Header = styled.div`
  display: grid;
  grid-template-areas:
    'logo name'
    'logo date';
  grid-template-columns: min-content 1fr;
  column-gap: var(--unit);
  align-items: center;
`;
const Logo = styled.img`
  grid-area: logo;
  width: 40px;
  height: 40px;
  justify-self: flex-end;
`;
const LeaderboardName = styled(Title4)`
  grid-area: name;
  text-align: left;
`;
const Date = styled.div`
  grid-area: date;
  text-align: left;
`;
const StyledMyClubLink = styled(MyClubLink)`
  background-color: rgba(var(--c-rgb-neutral-1000), 0.1);
  border-radius: var(--double-unit);
  padding: 2px var(--unit);
`;

const LINEUP_SHARING_QUERY = gql`
  query BaseballLineupSharingQuery($id: ID!) {
    so5 {
      so5Lineup(id: $id) {
        id
        so5LeaderboardContender {
          slug
          ...BaseballSo5Lineup_so5LeaderboardContender
        }
        so5Appearances {
          id
          captain
          pictureUrl
        }
        so5Leaderboard {
          slug
          so5League {
            slug
          }
          displayName
          iconUrl
          ...getLineupDisplayName_so5Leaderboard
        }
        so5Fixture {
          slug
          startDate
          ...LineupDate_fixture
        }
        socialPictureUrls {
          post
        }
        user {
          slug
          ...UserName_publicUserInfoInterface
          ...Avatar_publicUserInfoInterface
          ...MyClubLink_publicUserInfoInterface
        }
        ...getLineupDisplayName_so5Lineup
      }
    }
  }
  ${LineupDate.fragments.fixture}
  ${So5Lineup.fragments.so5LeaderboardContender}
  ${MyClubLink.fragments.user}
  ${UserName.fragments.user}
  ${Avatar.fragments.publicUserInfoInterface}
  ${getLineupDisplayName.fragments.so5Leaderboard}
  ${getLineupDisplayName.fragments.so5Lineup}
` as TypedDocumentNode<
  BaseballLineupSharingQuery,
  BaseballLineupSharingQueryVariables
>;

const LineupSharing = () => {
  const { setPageMetadata } = useSeoContext();
  const track = useFootballEvents();
  const { id } = useTypedParams<PageParams>();
  const { data } = useQuery(LINEUP_SHARING_QUERY, {
    variables: {
      id,
    },
  });

  const { so5Lineup } = data?.so5 || {};

  useEffect(() => {
    if (so5Lineup?.so5Leaderboard) {
      setPageMetadata(
        `${getLineupDisplayName({
          so5Leaderboard: so5Lineup.so5Leaderboard,
          so5Lineup,
        })}`,
        {
          img: so5Lineup?.socialPictureUrls.post,
        }
      );
    }
  }, [setPageMetadata, so5Lineup]);

  useEffect(() => {
    if (so5Lineup?.so5Leaderboard) {
      track('View Shared Lineup', {
        lineup: so5Lineup.id,
        competition: so5Lineup.so5Leaderboard.displayName,
      });
    }
  }, [so5Lineup, track]);

  if (!so5Lineup) return null;

  const { so5Leaderboard, so5LeaderboardContender, so5Fixture, user } =
    so5Lineup;

  return (
    <Root gap={3}>
      <Vertical center>
        <Header>
          <Logo src={so5Leaderboard?.iconUrl || ''} alt="" />
          <Date>
            <LineupDate fixture={so5Fixture} />
          </Date>
          <LeaderboardName>{so5Leaderboard?.displayName}</LeaderboardName>
        </Header>
        <StyledMyClubLink user={user}>
          <Horizontal as="span">
            <Avatar user={user} size={2} rounded />
            <LabelM>
              <UserName user={user} />
            </LabelM>
          </Horizontal>
        </StyledMyClubLink>
      </Vertical>
      <So5Lineup
        variant="twoRows"
        so5LeaderboardContender={so5LeaderboardContender}
      />
      <Vertical center>
        <Button
          to={generatePath(MLB_PLAY_PRO_FIXTURE_MY_LINEUPS_LINEUP, {
            fixture: so5Fixture.slug,
            lineup: idFromObject(so5Lineup.id),
          })}
          color="primary"
          size="medium"
        >
          <FormattedMessage {...glossary.viewDetails} />
        </Button>
        <Link to={FOOTBALL_PLAY}>
          <Text16 bold color="var(--c-neutral-1000)">
            <FormattedMessage
              id="LineupSharing.Cta.goBackHome"
              defaultMessage="Go back to home"
            />
          </Text16>
        </Link>
      </Vertical>
    </Root>
  );
};

export default LineupSharing;
